'use client';

import { type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppImage, ResponsiveImage } from '@/app/components/media/app-image';
import { APP_VIDEO_DEFAULTS } from '@/app/components/media/app-video/constants';
import type { AppVideoMediaType, AppVideoProps } from '@/app/components/media/app-video/types';
import { isResponsivePoster, normalizeVideoSrc, resolveMediaType } from '@/app/components/media/app-video/utils';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
import { playVideoWithPromiseHandle } from '@/app/utils/video';
export { ALLOWED_MEDIA } from '@/app/components/media/app-image';
export { APP_VIDEO_DEFAULTS } from '@/app/components/media/app-video/constants';
export type { AppVideoPoster, AppVideoPreload, AppVideoProps, AppVideoSrc } from '@/app/components/media/app-video/types';
export const AppVideo = ({
  poster,
  video,
  desktopMedia = APP_VIDEO_DEFAULTS.desktopMedia,
  autoplay = false,
  nativeControlsOnAutoplayFailure = false,
  paused = false,
  loop = false,
  preload = 'none',
  waitForFullBuffer = false,
  rewindWhenPaused = false,
  preventLoadVideo = false,
  priority = false,
  className,
  onReady,
  onElementMount,
  onVideoPlay,
  onVideoPlaying,
  onVideoPause,
  onVideoWaiting,
  onVideoSeeking,
  onVideoSeeked,
  onVideoEnded,
  onVideoError,
  onPlayRejected,
  onDurationChange,
  onProgress,
  onPosterLoaded,
  testId = APP_VIDEO_DEFAULTS.testId
}: AppVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressVideoRef = useRef<HTMLVideoElement | null>(null);
  const progressFrameRef = useRef<number | null>(null);
  const notifiedSrcRef = useRef<string | undefined>(undefined);
  const onPlayRejectedRef = useRef(onPlayRejected);
  const [mediaType, setMediaType] = useState<AppVideoMediaType | undefined>();
  const [canPlay, setCanPlay] = useState<boolean>(false);
  const [hasPresentedVideo, setHasPresentedVideo] = useState<boolean>(false);
  const [showNativeControls, setShowNativeControls] = useState<boolean>(false);
  const videoSources = useMemo(() => normalizeVideoSrc(video), [video]);
  const hasVideo = !!(videoSources.mobile && videoSources.desktop);

  // Resolve src as soon as media type is known (do not wait on poster onLoad).
  const videoSrc = preventLoadVideo || !mediaType ? undefined : videoSources[mediaType];
  const shouldNativeAutoplay = autoplay && !paused && !waitForFullBuffer;
  const nativePoster = isResponsivePoster(poster) ? poster[mediaType === 'desktop' ? 'desktopSrc' : 'mobileSrc'].src : poster.src;
  const syncMediaType = useCallback(() => {
    setMediaType(resolveMediaType(desktopMedia));
  }, [desktopMedia]);
  const handlePlayRejected = useCallback((error: unknown) => {
    if (nativeControlsOnAutoplayFailure && videoRef.current?.paused) {
      setShowNativeControls(true);
    }
    safeCall(onPlayRejectedRef.current, error);
  }, [nativeControlsOnAutoplayFailure]);
  const handlePosterReady = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    safeCall(poster.onLoad, event);
    safeCall(onPosterLoaded);
  }, [onPosterLoaded, poster]);
  const handlePosterError = useCallback((event_0: SyntheticEvent<HTMLImageElement>) => {
    safeCall(poster.onError, event_0);
  }, [poster]);
  const handleLoadedMetadata = useCallback((event_1: SyntheticEvent<HTMLVideoElement>) => {
    const el = event_1.currentTarget;
    // Keep metadata handling separate from playback readiness.
    if (!autoplay || paused) {
      el.pause();
      if (rewindWhenPaused) {
        el.currentTime = 0;
      }
    }

    // Notify ready once per resolved video src to avoid duplicate parent state updates.
    if (notifiedSrcRef.current === videoSrc) return;
    notifiedSrcRef.current = videoSrc;
    safeCall(onReady, el);
  }, [autoplay, onReady, paused, rewindWhenPaused, videoSrc]);
  const updateVideoProgress = useCallback((video_0: HTMLVideoElement) => {
    if (video_0.duration && Number.isFinite(video_0.duration)) {
      safeCall(onProgress, video_0.currentTime, video_0.duration);
    }
  }, [onProgress]);
  const cancelProgressLoop = useCallback(() => {
    if (progressFrameRef.current != null) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
    progressVideoRef.current = null;
  }, []);
  const progressLoop = useCallback(function updateProgressLoop() {
    const video_1 = progressVideoRef.current;
    if (!video_1) {
      progressFrameRef.current = null;
      return;
    }
    updateVideoProgress(video_1);
    if (video_1.paused || video_1.ended) {
      progressFrameRef.current = null;
      return;
    }
    progressFrameRef.current = window.requestAnimationFrame(updateProgressLoop);
  }, [updateVideoProgress]);
  const handleVideoPlay = useCallback((event_2: SyntheticEvent<HTMLVideoElement>) => {
    safeCall(onVideoPlay);
    updateVideoProgress(event_2.currentTarget);
  }, [onVideoPlay, updateVideoProgress]);
  const handleVideoPlaying = useCallback((event_3: SyntheticEvent<HTMLVideoElement>) => {
    setHasPresentedVideo(true);
    setShowNativeControls(false);
    safeCall(onVideoPlaying);
    if (!onProgress) return;
    const video_2 = event_3.currentTarget;
    progressVideoRef.current = video_2;
    updateVideoProgress(video_2);
    if (progressFrameRef.current == null) {
      progressFrameRef.current = window.requestAnimationFrame(progressLoop);
    }
  }, [onProgress, onVideoPlaying, progressLoop, updateVideoProgress]);
  const handleVideoPause = useCallback(() => {
    cancelProgressLoop();
    safeCall(onVideoPause);
  }, [cancelProgressLoop, onVideoPause]);
  const handleVideoWaiting = useCallback(() => {
    cancelProgressLoop();
    safeCall(onVideoWaiting);
  }, [cancelProgressLoop, onVideoWaiting]);
  const handleVideoSeeking = useCallback((event_4: SyntheticEvent<HTMLVideoElement>) => {
    cancelProgressLoop();
    updateVideoProgress(event_4.currentTarget);
    safeCall(onVideoSeeking);
  }, [cancelProgressLoop, onVideoSeeking, updateVideoProgress]);
  const handleVideoSeeked = useCallback((event_5: SyntheticEvent<HTMLVideoElement>) => {
    updateVideoProgress(event_5.currentTarget);
    safeCall(onVideoSeeked);
  }, [onVideoSeeked, updateVideoProgress]);
  const handleDurationChange = useCallback((event_6: SyntheticEvent<HTMLVideoElement>) => {
    const video_3 = event_6.currentTarget;
    updateVideoProgress(video_3);
    if (Number.isFinite(video_3.duration)) {
      safeCall(onDurationChange, video_3.duration);
    }
  }, [onDurationChange, updateVideoProgress]);
  const handleVideoError = useCallback(() => {
    cancelProgressLoop();
    safeCall(onVideoError);
  }, [cancelProgressLoop, onVideoError]);
  const updateBufferReadiness = useCallback((video_4: HTMLVideoElement) => {
    if (!waitForFullBuffer) {
      setCanPlay(true);
      return;
    }
    if (!Number.isFinite(video_4.duration) || video_4.buffered.length === 0) {
      return;
    }
    const bufferedEnd = video_4.buffered.end(video_4.buffered.length - 1);
    if (bufferedEnd >= video_4.duration - 0.1) {
      setCanPlay(true);
    }
  }, [waitForFullBuffer]);
  const handleCanPlay = useCallback((event_7: SyntheticEvent<HTMLVideoElement>) => {
    updateBufferReadiness(event_7.currentTarget);
  }, [updateBufferReadiness]);
  const handleCanPlayThrough = useCallback((event_8: SyntheticEvent<HTMLVideoElement>) => {
    updateBufferReadiness(event_8.currentTarget);
  }, [updateBufferReadiness]);
  const handleBufferProgress = useCallback((event_9: SyntheticEvent<HTMLVideoElement>) => {
    updateBufferReadiness(event_9.currentTarget);
  }, [updateBufferReadiness]);
  const handleVideoEnded = useCallback((event_10: SyntheticEvent<HTMLVideoElement>) => {
    const video_5 = event_10.currentTarget;
    cancelProgressLoop();
    if (video_5.duration && Number.isFinite(video_5.duration)) {
      safeCall(onProgress, video_5.duration, video_5.duration);
    }
    safeCall(onVideoEnded);
  }, [cancelProgressLoop, onProgress, onVideoEnded]);
  const handleTimeUpdate = useCallback((event_11: SyntheticEvent<HTMLVideoElement>) => {
    updateVideoProgress(event_11.currentTarget);
  }, [updateVideoProgress]);
  useEffect(() => {
    notifiedSrcRef.current = undefined;
    setCanPlay(false);
    setHasPresentedVideo(false);
    setShowNativeControls(false);
  }, [videoSrc, waitForFullBuffer]);
  useEffect(() => {
    onPlayRejectedRef.current = onPlayRejected;
  }, [onPlayRejected]);
  const onElementMountRef = useRef(onElementMount);
  useEffect(() => {
    onElementMountRef.current = onElementMount;
  }, [onElementMount]);

  // Once per src: the element is rendered whenever `videoSrc` is set.
  useEffect(() => {
    const el_0 = videoRef.current;
    if (!el_0 || !videoSrc) return;
    safeCall(onElementMountRef.current, el_0);
  }, [videoSrc]);
  useEffect(() => cancelProgressLoop, [cancelProgressLoop]);
  useEffect(() => {
    syncMediaType();
    const mediaQuery = window.matchMedia(desktopMedia);
    const onChange = () => syncMediaType();
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [desktopMedia, syncMediaType]);
  useEffect(() => {
    if (!hasVideo) {
      safeCall(onReady);
    }
  }, [hasVideo, onReady]);
  useEffect(() => {
    const el_1 = videoRef.current;
    if (!el_1 || !videoSrc) return;
    if (paused) {
      if (hasPresentedVideo && rewindWhenPaused) {
        el_1.currentTime = 0;
      }
      if (!el_1.paused) {
        el_1.pause();
      }
      return;
    }
    if (!autoplay || waitForFullBuffer && !canPlay || !el_1.paused) return;

    // The browser dispatches `pause` immediately before `ended`. Rewinding here
    // would race the ended callback and implicitly loop a non-looping video.
    if (el_1.ended) return;
    playVideoWithPromiseHandle(el_1, undefined, handlePlayRejected);
  }, [autoplay, canPlay, handlePlayRejected, hasPresentedVideo, paused, rewindWhenPaused, videoSrc, waitForFullBuffer]);
  const posterNode = isResponsivePoster(poster) ? <ResponsiveImage mobileSrc={poster.mobileSrc} desktopSrc={poster.desktopSrc} alt={poster.alt} fill preload={priority} loading={priority ? 'eager' : poster.loading} fetchPriority={poster.fetchPriority} preloadOffset={poster.preloadOffset} objectFit="cover" className={cn('absolute inset-0 size-full', hasPresentedVideo && 'invisible')} testId={`${testId}-poster`} onLoad={handlePosterReady} onError={handlePosterError} /> : <AppImage src={poster.src} alt={poster.alt} fill sizes={poster.sizes ?? APP_VIDEO_DEFAULTS.posterSizes} params={poster.params} preload={priority} loading={priority ? 'eager' : poster.loading} fetchPriority={poster.fetchPriority} preloadOffset={poster.preloadOffset} objectFit="cover" className={cn('absolute inset-0 size-full', hasPresentedVideo && 'invisible')} testId={`${testId}-poster`} onLoad={handlePosterReady} onError={handlePosterError} />;
  return <div className={cn('absolute inset-0 w-full', className)} {...createDataAttributes({
    test: testId
  })}>
      {posterNode}
      {!!videoSrc && <video ref={videoRef} preload={preload} autoPlay={shouldNativeAutoplay} controls={showNativeControls} poster={nativeControlsOnAutoplayFailure ? nativePoster : undefined} muted playsInline className={cn('absolute inset-0 h-0 max-h-fit min-h-full w-0 max-w-full min-w-full object-cover object-center', hasPresentedVideo || showNativeControls ? 'opacity-100' : 'opacity-0', showNativeControls && 'pointer-events-auto')} src={videoSrc} onLoadedMetadata={handleLoadedMetadata} onCanPlay={handleCanPlay} onCanPlayThrough={handleCanPlayThrough} onPlay={handleVideoPlay} onPlaying={handleVideoPlaying} onPause={handleVideoPause} onWaiting={handleVideoWaiting} onSeeking={handleVideoSeeking} onSeeked={handleVideoSeeked} onDurationChange={handleDurationChange} onError={handleVideoError} onProgress={preload === 'auto' ? handleBufferProgress : undefined} onTimeUpdate={onProgress ? handleTimeUpdate : undefined} onEnded={onVideoEnded ? handleVideoEnded : undefined} loop={loop} {...createDataAttributes({
      test: `${testId}-element`
    })} />}
    </div>;
};